import {
  Badge,
  EmptyState,
  FlexBox,
  InputSelectionListProps,
  SelectionList,
  Spinner,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import {
  HookFormFieldWrappedBaseProps,
  HookFormSelectionList,
} from 'lib-shared-hook-form';
import { formatInternational } from 'lib-shared-phone-numbers';
import { PropsWithChildren } from 'lib-shared-types-utils';
import isEqual from 'lodash/isEqual';
import { ComponentProps, useCallback, useMemo, useState } from 'react';
import { FieldPath, FieldValues, useWatch } from 'react-hook-form';
import { useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { numbersApi, PhoneNumber, ReadNumbersApiArg } from 'apis/numbers';
import { PhoneNumberCountryFlag } from 'components/phoneNumberCountryFlag/phoneNumberCountryFlag';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';

import styles from './hookFormPhoneNumberSelect.module.scss';

type HookFormPhoneNumberSelectProps = Partial<
  Omit<InputSelectionListProps<PhoneNumber>, 'children'>
> &
  Pick<ReadNumbersApiArg, 'capability'> & {
    /**
     * Whether to show the flag of the selected number in the prefix and in the
     * selection list.
     */
    showCountryFlag?: boolean;
    /** Whether to show that the phone number supports recordings. */
    showRecordingInfo?: boolean;
  };

/**
 * Represents the React Hook Form variant of the {@link InputSelectionList}
 * component for selecting phone numbers.
 */
export const HookFormPhoneNumberSelect = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  children,
  ...props
}: HookFormFieldWrappedBaseProps<
  TFieldValues,
  TName,
  HookFormPhoneNumberSelectProps
> &
  PropsWithChildren): JSX.Element => {
  const {
    capability,
    showCountryFlag = false,
    showRecordingInfo = false,
    ...componentProps
  } = props.componentProps ?? {};

  const {
    asyncStatus,
    items: phoneNumbers,
    changeArg,
    hasNextPage,
    nextPage,
  } = usePaginatedEndpoint({
    endpoint: numbersApi.endpoints.readNumbers,
    infinite: true,
    itemsSelector: (data) => data.data.numbers,
    initialArg: { capability, limit: 20 },
  });

  const [searchTerm, setSearchTerm] = useState('');

  useUpdateEffect(() => {
    changeArg({ searchTerm: searchTerm || undefined }, { debounceTime: 300 });
  }, [changeArg, searchTerm]);

  const selectedValue = useWatch({
    control: props.control,
    name: props.name,
  });

  const renderRecordingBadge = useCallback(
    (phoneNumber: PhoneNumber): JSX.Element | null => {
      if (!showRecordingInfo || !phoneNumber.record_outbound_calls) return null;

      return (
        <Tooltip
          content={`Outbound call recording is active on ${formatInternational(
            phoneNumber.e164_format,
          )}`}
        >
          <Badge variant="error" size="small">
            REC
          </Badge>
        </Tooltip>
      );
    },
    [showRecordingInfo],
  );

  const inputSelectionListProps = useMemo(
    (): ComponentProps<
      typeof HookFormSelectionList<PhoneNumber>
    >['componentProps'] => ({
      placeholder: 'Select a phone number',
      prefix: showCountryFlag
        ? {
            type: 'custom',
            value: (
              <PhoneNumberCountryFlag
                phoneNumber={selectedValue?.[0]?.e164_format}
              />
            ),
            noWrap: false,
          }
        : undefined,
      equalityFn: isEqual,
      selectionListProps: {
        className: styles.selectionList,
      },
      filter: {
        value: searchTerm,
        onChange: (e) => setSearchTerm(e.target.value),
      },
      getItemTitleByValue: (phoneNumber) => {
        if (!phoneNumber) return null;

        return (
          <FlexBox gap="300" alignItems="center">
            <Text.Paragraph>{`${formatInternational(phoneNumber.e164_format)} ${
              phoneNumber.name ? `(${phoneNumber.name})` : ''
            }`}</Text.Paragraph>

            {renderRecordingBadge(phoneNumber)}
          </FlexBox>
        );
      },
      onToggleDropdown: (isOpen: boolean) => !isOpen && setSearchTerm(''),
      ...componentProps,
    }),
    [
      componentProps,
      searchTerm,
      selectedValue,
      showCountryFlag,
      renderRecordingBadge,
    ],
  );

  return (
    <HookFormSelectionList {...props} componentProps={inputSelectionListProps}>
      {phoneNumbers.map((phoneNumber) => (
        <SelectionList.Item
          key={phoneNumber.e164_format}
          value={phoneNumber}
          isDisabled={
            capability === 'sms' &&
            phoneNumber.sms_functionality_state === 'deactivated'
          }
          noFixedSelectionStateSpace={showCountryFlag}
          contentClassName={styles.selectionListItemContent}
        >
          {showCountryFlag && (
            <PhoneNumberCountryFlag phoneNumber={phoneNumber.e164_format} />
          )}

          <div className={styles.selectionListItemText}>
            <Text.Paragraph>
              {formatInternational(phoneNumber.e164_format)}
            </Text.Paragraph>

            <Text.Paragraph subdued>{phoneNumber.name}</Text.Paragraph>
          </div>

          {renderRecordingBadge(phoneNumber)}
        </SelectionList.Item>
      ))}

      {asyncStatus === 'pending' && (
        <FlexBox as="li" padding="300" justifyContent="center">
          <Spinner size={20} />
        </FlexBox>
      )}

      {asyncStatus === 'fulfilled' && !phoneNumbers.length && (
        <FlexBox as="li" padding="300" justifyContent="center">
          <EmptyState
            title={
              searchTerm
                ? 'No phone numbers found'
                : 'No phone numbers available'
            }
            description={
              searchTerm
                ? 'Try changing your search criteria.'
                : 'Your organization has no phone numbers available.'
            }
          />
        </FlexBox>
      )}

      {asyncStatus === 'fulfilled' && hasNextPage && (
        <Waypoint onEnter={nextPage} />
      )}
    </HookFormSelectionList>
  );
};
